// Custos da equipe e do escritório. Leitura e escrita das tabelas só funcionam
// para admin (RLS). Quem não é admin usa apenas getRoleSaleRates.
import { supabase } from '../lib/supabase';
import type {
  MemberBreakdown,
  NewRateInput,
  OfficeCost,
  OfficeCostDraft,
  OfficeCostSettings,
  SaleRates,
  TeamCostBreakdown,
  TeamCostMember,
  TeamCostRate,
} from '../types/teamCost';
import type {
  NewRoleRateInput,
  RoleBreakdown,
  RoleCostBreakdown,
  RoleRate,
  RoleSaleRates,
} from '../types/roleCost';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

const num = (v: unknown): number => Number(v) || 0;
const numOrNull = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));

// Separa o rascunho de um bloco de custos em apagar / atualizar / inserir.
// Linha já gravada cuja descrição foi apagada conta como remoção: ignorá-la
// deixaria o custo antigo valendo sem o usuário perceber.
export function partitionOfficeCostDrafts(
  rows: OfficeCostDraft[],
  removedIds: string[],
): { toDelete: string[]; toUpsert: OfficeCostDraft[]; toInsert: OfficeCostDraft[] } {
  const blank = (r: OfficeCostDraft) => r.description.trim().length === 0;
  const cleared = rows.filter((r) => r.id && blank(r)).map((r) => r.id as string);
  const toDelete = Array.from(new Set([...removedIds, ...cleared]));
  const clean = rows.filter((r) => !blank(r));
  return {
    toDelete,
    toUpsert: clean.filter((r) => r.id),
    toInsert: clean.filter((r) => !r.id),
  };
}

type Raw = Record<string, unknown>;

const toRate = (r: Raw): TeamCostRate => ({
  ...(r as unknown as TeamCostRate),
  monthly_amount: numOrNull(r.monthly_amount),
  charges_pct: numOrNull(r.charges_pct),
  productive_hours_month: numOrNull(r.productive_hours_month),
  hourly_amount: numOrNull(r.hourly_amount),
});

const toRoleRate = (r: Raw): RoleRate => ({
  ...(r as unknown as RoleRate),
  monthly_amount: numOrNull(r.monthly_amount),
  charges_pct: numOrNull(r.charges_pct),
  productive_hours_month: numOrNull(r.productive_hours_month),
  hourly_amount: numOrNull(r.hourly_amount),
});

const toOfficeCost = (r: Raw): OfficeCost => ({
  ...(r as unknown as OfficeCost),
  monthly_amount: num(r.monthly_amount),
});

const toSettings = (r: Raw): OfficeCostSettings => ({
  organization_id: String(r.organization_id),
  default_charges_pct: num(r.default_charges_pct),
  default_productive_hours_month: num(r.default_productive_hours_month),
  profit_margin_pct: num(r.profit_margin_pct),
});

export const teamCostService = {
  normalizeSaleRates(raw: unknown): SaleRates {
    const obj = (raw ?? {}) as Raw;
    const members = Array.isArray(obj.members) ? (obj.members as Raw[]) : [];
    return {
      members: members.map((m) => ({
        member_id: String(m.member_id),
        kind: m.kind as SaleRates['members'][number]['kind'],
        user_id: (m.user_id as string | null) ?? null,
        name: (m.name as string | null) ?? null,
        role_label: (m.role_label as string | null) ?? null,
        sale_hourly_rate: num(m.sale_hourly_rate),
      })),
      weighted_avg_sale_rate: numOrNull(obj.weighted_avg_sale_rate),
    };
  },

  normalizeBreakdown(raw: unknown): TeamCostBreakdown {
    const obj = (raw ?? {}) as Raw;
    const members = Array.isArray(obj.members) ? (obj.members as Raw[]) : [];
    const t = (obj.totals ?? {}) as Raw;
    return {
      members: members.map((m): MemberBreakdown => ({
        member_id: String(m.member_id),
        kind: m.kind as MemberBreakdown['kind'],
        user_id: (m.user_id as string | null) ?? null,
        name: (m.name as string | null) ?? null,
        role_label: (m.role_label as string | null) ?? null,
        pay_type: (m.pay_type as MemberBreakdown['pay_type']) ?? null,
        monthly_amount: numOrNull(m.monthly_amount),
        hourly_amount: numOrNull(m.hourly_amount),
        charges_pct: numOrNull(m.charges_pct),
        productive_hours: numOrNull(m.productive_hours),
        cost_hourly: numOrNull(m.cost_hourly),
        overhead_hourly: num(m.overhead_hourly),
        sale_hourly_rate: numOrNull(m.sale_hourly_rate),
        incomplete: Boolean(m.incomplete),
      })),
      totals: {
        office_monthly_cost: num(t.office_monthly_cost),
        hours_pool: num(t.hours_pool),
        overhead_hourly: num(t.overhead_hourly),
        margin_pct: num(t.margin_pct),
        no_hours_pool: Boolean(t.no_hours_pool),
      },
    };
  },

  async getSaleHourlyRates(orgId: string, date?: string): Promise<SaleRates> {
    const { data, error } = await db.rpc('get_sale_hourly_rates', { p_org: orgId, ...(date ? { p_date: date } : {}) });
    if (error) throw error;
    return teamCostService.normalizeSaleRates(data);
  },

  async getBreakdown(orgId: string, date?: string): Promise<TeamCostBreakdown> {
    const { data, error } = await db.rpc('get_team_cost_breakdown', { p_org: orgId, ...(date ? { p_date: date } : {}) });
    if (error) throw error;
    return teamCostService.normalizeBreakdown(data);
  },

  normalizeRoleSaleRates(raw: unknown): RoleSaleRates {
    const obj = (raw ?? {}) as Raw;
    const roles = Array.isArray(obj.roles) ? (obj.roles as Raw[]) : [];
    return {
      roles: roles.map((r) => ({
        job_role_id: String(r.job_role_id),
        name: String(r.name ?? ''),
        is_external: Boolean(r.is_external),
        sale_hourly_rate: numOrNull(r.sale_hourly_rate),
      })),
      weighted_avg_sale_rate: numOrNull(obj.weighted_avg_sale_rate),
    };
  },

  normalizeRoleBreakdown(raw: unknown): RoleCostBreakdown {
    const obj = (raw ?? {}) as Raw;
    const roles = Array.isArray(obj.roles) ? (obj.roles as Raw[]) : [];
    const t = (obj.totals ?? {}) as Raw;
    return {
      roles: roles.map((r): RoleBreakdown => ({
        job_role_id: String(r.job_role_id),
        name: String(r.name ?? ''),
        is_external: Boolean(r.is_external),
        headcount: num(r.headcount),
        pay_type: (r.pay_type as RoleBreakdown['pay_type']) ?? null,
        monthly_amount: numOrNull(r.monthly_amount),
        hourly_amount: numOrNull(r.hourly_amount),
        charges_pct: numOrNull(r.charges_pct),
        productive_hours: numOrNull(r.productive_hours),
        cost_hourly: numOrNull(r.cost_hourly),
        overhead_hourly: num(r.overhead_hourly),
        sale_hourly_rate: numOrNull(r.sale_hourly_rate),
        incomplete: Boolean(r.incomplete),
      })),
      totals: {
        office_monthly_cost: num(t.office_monthly_cost),
        hours_pool: num(t.hours_pool),
        overhead_hourly: num(t.overhead_hourly),
        margin_pct: num(t.margin_pct),
        no_hours_pool: Boolean(t.no_hours_pool),
      },
    };
  },

  async getRoleBreakdown(orgId: string, date?: string): Promise<RoleCostBreakdown> {
    const { data, error } = await db.rpc('get_role_cost_breakdown', { p_org: orgId, ...(date ? { p_date: date } : {}) });
    if (error) throw error;
    return teamCostService.normalizeRoleBreakdown(data);
  },

  async getRoleSaleRates(orgId: string, date?: string): Promise<RoleSaleRates> {
    const { data, error } = await db.rpc('get_role_sale_rates', { p_org: orgId, ...(date ? { p_date: date } : {}) });
    if (error) throw error;
    return teamCostService.normalizeRoleSaleRates(data);
  },

  async listRoleRates(orgId: string): Promise<RoleRate[]> {
    const { data, error } = await db
      .from('job_role_rates')
      .select('*')
      .eq('organization_id', orgId)
      .order('valid_from', { ascending: false });
    if (error) throw error;
    return ((data ?? []) as Raw[]).map(toRoleRate);
  },

  // Mesmo dia substitui; outro dia cria nova vigência. organization_id vem do
  // cargo (trigger).
  async saveRoleRate(input: NewRoleRateInput): Promise<RoleRate> {
    const monthly = input.pay_type === 'monthly';
    const { data, error } = await db
      .from('job_role_rates')
      .upsert(
        {
          job_role_id: input.job_role_id,
          valid_from: input.valid_from,
          pay_type: input.pay_type,
          monthly_amount: monthly ? input.monthly_amount ?? 0 : null,
          charges_pct: monthly ? input.charges_pct ?? null : null,
          productive_hours_month: monthly ? input.productive_hours_month ?? null : null,
          hourly_amount: monthly ? null : input.hourly_amount ?? 0,
        },
        { onConflict: 'job_role_id,valid_from' },
      )
      .select()
      .single();
    if (error) throw error;
    return toRoleRate(data as Raw);
  },

  async amIOrgAdmin(orgId: string): Promise<boolean> {
    const { data, error } = await db.rpc('am_i_org_admin', { p_org: orgId });
    if (error) throw error;
    return data === true;
  },

  async listMembers(orgId: string): Promise<TeamCostMember[]> {
    const { data, error } = await db.from('team_cost_members').select('*').eq('organization_id', orgId);
    if (error) throw error;
    return (data ?? []) as TeamCostMember[];
  },

  async listRates(orgId: string): Promise<TeamCostRate[]> {
    const { data, error } = await db
      .from('team_cost_rates')
      .select('*')
      .eq('organization_id', orgId)
      .order('valid_from', { ascending: false });
    if (error) throw error;
    return ((data ?? []) as Raw[]).map(toRate);
  },

  async setInternalMemberActive(orgId: string, userId: string, active: boolean): Promise<TeamCostMember> {
    const { data, error } = await db
      .from('team_cost_members')
      .upsert({ organization_id: orgId, kind: 'internal', user_id: userId, active }, { onConflict: 'organization_id,user_id' })
      .select()
      .single();
    if (error) throw error;
    return data as TeamCostMember;
  },

  async createExternalMember(
    orgId: string,
    input: { name: string; email?: string | null; role_label?: string | null },
  ): Promise<TeamCostMember> {
    const { data, error } = await db
      .from('team_cost_members')
      .insert({
        organization_id: orgId,
        kind: 'external',
        name: input.name.trim(),
        email: input.email?.trim() || null,
        role_label: input.role_label?.trim() || null,
      })
      .select()
      .single();
    if (error) throw error;
    return data as TeamCostMember;
  },

  async updateMember(
    id: string,
    patch: Partial<Pick<TeamCostMember, 'name' | 'email' | 'role_label' | 'active'>>,
  ): Promise<void> {
    const { error } = await db.from('team_cost_members').update(patch).eq('id', id);
    if (error) throw error;
  },

  // Mesmo dia substitui; outro dia cria nova vigência. organization_id é
  // preenchido pelo trigger a partir do membro.
  async saveRate(input: NewRateInput): Promise<TeamCostRate> {
    const monthly = input.pay_type === 'monthly';
    const { data, error } = await db
      .from('team_cost_rates')
      .upsert(
        {
          member_id: input.member_id,
          valid_from: input.valid_from,
          pay_type: input.pay_type,
          monthly_amount: monthly ? input.monthly_amount ?? 0 : null,
          charges_pct: monthly ? input.charges_pct ?? null : null,
          productive_hours_month: monthly ? input.productive_hours_month ?? null : null,
          hourly_amount: monthly ? null : input.hourly_amount ?? 0,
        },
        { onConflict: 'member_id,valid_from' },
      )
      .select()
      .single();
    if (error) throw error;
    return toRate(data as Raw);
  },

  async getSettings(orgId: string): Promise<OfficeCostSettings | null> {
    const { data, error } = await db
      .from('office_cost_settings')
      .select('*')
      .eq('organization_id', orgId)
      .maybeSingle();
    if (error) throw error;
    return data ? toSettings(data as Raw) : null;
  },

  async saveSettings(s: OfficeCostSettings): Promise<void> {
    const { error } = await db.from('office_cost_settings').upsert(s, { onConflict: 'organization_id' });
    if (error) throw error;
  },

  async listOfficeCosts(orgId: string): Promise<OfficeCost[]> {
    const { data, error } = await db
      .from('office_costs')
      .select('*')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return ((data ?? []) as Raw[]).map(toOfficeCost);
  },

  async saveOfficeCosts(orgId: string, rows: OfficeCostDraft[], removedIds: string[]): Promise<void> {
    const { toDelete, toUpsert: existing, toInsert: fresh } = partitionOfficeCostDrafts(rows, removedIds);
    if (toDelete.length > 0) {
      const { error } = await db.from('office_costs').delete().in('id', toDelete);
      if (error) throw error;
    }
    if (existing.length > 0) {
      const { error } = await db.from('office_costs').upsert(
        existing.map((r) => ({
          id: r.id,
          organization_id: orgId,
          description: r.description.trim(),
          kind: r.kind,
          monthly_amount: r.monthly_amount,
        })),
      );
      if (error) throw error;
    }
    if (fresh.length > 0) {
      const { error } = await db.from('office_costs').insert(
        fresh.map((r) => ({
          organization_id: orgId,
          description: r.description.trim(),
          kind: r.kind,
          monthly_amount: r.monthly_amount,
        })),
      );
      if (error) throw error;
    }
  },
};
